
import { defineComponent, reactive, computed } from 'vue'

import Modal from '@/common/components/UI/Modal/index.vue'
import FileReader from '@/common/components/UI/FileReader/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'

import useNotifications from '@/common/composable/useNotifications'

import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'

import type { CostStandardCheckImportFileItem, CostStandardImportLtlPayload, CostStandardImportLtlPayloadCostStandards } from '@/new-services/types/tms'
import tmsAPI from '@/new-services/tms.service'

const INVOICE_TYPES = [
    { id: 1, name: 'LTL' },
    { id: 2, name: 'FTL' },
]

export default defineComponent({
    components: {
        Modal,
        FileReader,
        TheButton,
        TheSelect,
        Loader
    },
    emits: ['cancel', 'confirm'],
    setup(_, { emit }) {
        const notifications = useNotifications()

        const state = reactive({
            is_loading: false,
            file: [] as File[],
            invoiceType: 1,
        })

        const check_state = reactive({
            is_loading: false,
            is_failed: false,
            items: [] as CostStandardCheckImportFileItem[],
            warnings: [] as CostStandardCheckImportFileItem[],
            validated: [] as CostStandardCheckImportFileItem[]
        })

        const state_form$ = useVuelidate(
            {
                file: { required },
            },
            state
        )

        const emitCancel = () => {
            if (state.is_loading) return
            emit('cancel')
        }

        const costStandardCheckImportFile = async (file: File) => {
            check_state.items = []
            check_state.warnings = []
            check_state.validated = []

            try{
                check_state.is_loading = true

                const form_data = new FormData()
                form_data.append('file', file)

                const response = await tmsAPI.costStandardCheckImportFile(form_data)

                check_state.items = response.data.data
                check_state.warnings = response.data.data.filter((item) => !item.isSuccess)
                check_state.validated = response.data.data.filter((item) => item.isSuccess)
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При проверке файла произошла ошибка' })
            } finally { 
                check_state.is_loading = false
            }
        }

        const onFileChange = (files: File[]) => {
            if (state.invoiceType === 1) {
                if (files.length > 0) {
                    const file = files[0]
                    costStandardCheckImportFile(file)
                } else {
                    check_state.items = []
                    check_state.warnings = []
                    check_state.validated = []
                }
            }

            return false
        }

        const arraysEqual = (a: number[], b: number[]): boolean => {
            if (a.length !== b.length) return false
            return a.every((value, index) => value === b[index])
        }

        const getLtlPayload = (): CostStandardImportLtlPayload => {
            const costStandards: Array<CostStandardImportLtlPayloadCostStandards> = []

            check_state.validated.forEach((item) => {
                if (item.isSuccess) {
                    const itemCityIds = item.cities as number[]
                    const index = costStandards.findIndex((costStandard) => arraysEqual(itemCityIds, costStandard.cityIds))

                    if (index >= 0) {
                        costStandards[index].costPerWeightByMonth.push({
                            monthId: item.monthId,
                            costPerWeight: item.costPerWeight
                        })
                    } else {
                        costStandards.push({ 
                            cityIds: itemCityIds,
                            costPerWeightByMonth: [
                                {
                                    monthId: item.monthId,
                                    costPerWeight: item.costPerWeight
                                }
                            ]
                        })
                    }
                }
            })

            return { costStandards }
        }

        const onInvoiceTypeChange = () => {
            state.file = []
            check_state.items = []
            check_state.warnings = []
            check_state.validated = []
        }

        const submitExcel = async () => {
            if (check_state.is_loading) return

            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            try {
                state.is_loading = true

                if (state.invoiceType === 1) {
                    const payload = getLtlPayload()
                    await tmsAPI.costStandardImportLtl(payload)
                } else {
                    const form_data = new FormData()
                    form_data.append('invoiceType', state.invoiceType.toString())
                    form_data.append('file', state.file[0])

                    await tmsAPI.importExcelCostStandard(form_data)
                }

                notifications.notify({
                    type: 'success',
                    title: 'Нормы себестоимости успешно импортированы!',
                })

                emit('confirm')
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'Произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        const isLtl = computed(() => state.invoiceType === 1)

        const getTemplateLink = computed(() => {
            return isLtl.value
                ? '/Шаблон импорта норм себестоимости LTL.xlsx'
                : '/Шаблон импорта норм себестоимости FTL.xlsx'
        })
        const getTemplateName = computed(() => {
            return isLtl.value
                ? 'Шаблон импорта норм себестоимости LTL.xlsx'
                : 'Шаблон импорта норм себестоимости FTL.xlsx'
        })

        return {
            state,
            check_state,
            state_form$,
            emitCancel,
            submitExcel,
            onFileChange,
            INVOICE_TYPES,
            getTemplateLink,
            getTemplateName,
            onInvoiceTypeChange,
        }
    },
})
